// 个券 IRR 计算器的行与选项（一处定义，组件只渲染）。口径说明文案在 chong_ui_tooltip_config 的 irr.calculator_help。
// kind: rate = 比例（显示 %），amount = 金额（元，显示万元）。editable 的行可逐格修改，改动只存这些驱动项。
// stat: 历史区间取 baseline.history.stats 的哪一项（占营业收入的比例，近 12 个季度）。
export const ROWS = [
  { key: 'growth', label: '营业收入增速', kind: 'rate', editable: true },
  { key: 'revenue', label: '总营业收入', kind: 'amount', editable: true, strong: true },
  { key: 'noi_margin', label: 'NOI Margin', kind: 'rate', editable: true, stat: 'noi' },
  { key: 'noi', label: 'NOI', kind: 'amount', strong: true },
  { key: 'ebitda_margin', label: 'EBITDA Margin', kind: 'rate', editable: true, stat: 'ebitda' },
  { key: 'ebitda', label: 'EBITDA', kind: 'amount', strong: true },
  // 杠杆（leverage 行：没有有息负债也没有利息的基金不显示）：FFO、可分配比例已含近一年节奏的利息与还本，改利率/本金只传差额
  { key: 'debt', label: '有息负债余额', kind: 'amount', leverage: true },
  { key: 'interest_rate', label: '借款利率', kind: 'rate', editable: true, leverage: true },
  { key: 'interest', label: '利息费用', kind: 'amount', leverage: true },
  { key: 'ffo_margin', label: 'FFO Margin', kind: 'rate', editable: true, stat: 'ffo' },
  { key: 'ffo', label: 'FFO', kind: 'amount', strong: true },
  { key: 'principal', label: '偿还本金', kind: 'amount', editable: true, leverage: true },
  { key: 'div_margin', label: 'Div Margin', kind: 'rate', editable: true, stat: 'distributable_abmod' },
  { key: 'distributable', label: '可供分配金额', kind: 'amount', strong: true },
  { key: 'cash_yield', label: '经营部分 - 预测分派率', kind: 'rate', highlight: true },
];

// 预测方式：第 0 期（最新 TTM）及之前都是实际数，之后的年份按所选方式生成；note 显示在表格下方。
// 完整计算（PPT 第 3 页）的行。editable 的行可逐格修改；比例行都是占营业收入的比例。
export const FULL_ROWS = [
  { key: 'growth', label: '营业收入增速', kind: 'rate', editable: true },
  { key: 'revenue', label: '总营业收入', kind: 'amount', editable: true, strong: true },
  { key: 'noi_margin', label: 'NOI Margin', kind: 'rate', editable: true, stat: 'noi' },
  { key: 'noi', label: 'NOI', kind: 'amount', strong: true },
  { key: 'fee_ratio', label: '基金管理人费率', kind: 'rate', editable: true },
  { key: 'mgt_fee', label: '基金管理人费', kind: 'amount' },
  { key: 'custody_ratio', label: '托管费率', kind: 'rate', editable: true },
  { key: 'custody_fee', label: '基金托管费', kind: 'amount' },
  { key: 'ebitda', label: 'EBITDA', kind: 'amount', strong: true },
  { key: 'struct_ratio', label: '结构调整比例', kind: 'rate', editable: true },
  { key: 'struct_adj', label: '结构调整', kind: 'amount' },
  { key: 'ffo_full', label: '无杠杆 FFO（加回利息，未扣税盾）', kind: 'amount', strong: true },
  { key: 'debt', label: '有息负债余额', kind: 'amount' },
  { key: 'interest_rate', label: '借款利率', kind: 'rate', editable: true },
  { key: 'interest', label: '利息费用', kind: 'amount' },
  { key: 'ffo_levered', label: 'FFO（扣利息）', kind: 'amount', strong: true },
  { key: 'capex_ratio', label: 'CAPEX 比例', kind: 'rate', editable: true },
  { key: 'capex', label: 'CAPEX', kind: 'amount' },
  { key: 'affo', label: 'AFFO（FFO − CAPEX）', kind: 'amount', strong: true },
  { key: 'principal', label: '偿还本金', kind: 'amount', editable: true },
  { key: 'reserve_ratio', label: '现金调节比例', kind: 'rate', editable: true },
  { key: 'cash_adj', label: '现金调节及预留', kind: 'amount' },
  { key: 'distributable', label: '可供分配金额', kind: 'amount', strong: true },
  { key: 'cash_yield', label: '经营部分 - 预测分派率', kind: 'rate', highlight: true },
];

export const MODES = [
  { value: 'simple', label: '简版计算' },
  { value: 'full', label: '完整计算' },
];

// 经营层 KPI（单价 × 数量）。kind 来自基础数据 operating.kind；fields 选用的列决定名称（有效租金 / 面价租金、平均 / 时点出租率）。
// format: rate = 百分比，number = 数字（digits 位小数）
export const OPERATING_KINDS = {
  rent: {
    price: { p_eff: '综合等效租金', p_face: '平均租金', unit: '元/㎡/日', digits: 2 },
    qty: { q_avg: '平均出租率', q_point: '时点出租率', format: 'rate' },
    growth: '租金增幅',
  },
  service: {
    price: { p_eff: '综合等效服务单价', p_face: '平均服务单价', unit: '元/kW/月', digits: 2 },
    qty: { q_avg: '平均上架率', q_point: '时点上架率', format: 'rate' },
    growth: '服务单价增幅',
  },
  energy: {
    price: { label: '平均结算电价', unit: '元/千瓦时', digits: 4 },
    qty: { label: '结算电量', unit: '万千瓦时', format: 'number', digits: 0 },
    growth: '电价增幅',
  },
  toll: {
    price: { label: '单车通行费', unit: '元/辆', digits: 2 },
    qty: { label: '日均车流量', unit: '辆/日', format: 'number', digits: 0 },
    growth: '单车通行费增幅',
  },
};

export const PRESETS = [
  { value: 'forecast', label: '评估报告现金流', note: '营业收入取评估报告逐年预测值，预测期以外按系统增速；各级比例沿用最新实际' },
  { value: 'linear', label: '历史线性外推', note: '近 12 个季度 TTM 营业收入线性回归外推（外推年数不超过历史覆盖年数），之后按系统增速；各级比例沿用最新实际' },
  { value: 'custom', label: '自定义', note: '每个预测年：营业收入 = 上一年 ×（1 + 增速），各级金额 = 营业收入 × 对应比例；默认值为系统增速与最新实际比例' },
  { value: 'operating', label: '经营数据推算', note: '营业收入 = 最新实际 ×（单价 × 数量）÷（最新单价 × 最新数量），单价按增幅逐年滚动（默认系统增速），数量默认保持最新；经营层 KPI 与底层资产运营数据同源（近 4 季口径）' },
];
export const FULL_NOTE = '完整计算：NOI − 基金管理人费 − 托管费 = EBITDA；− 结构调整 = FFO（全投资）；− 利息 − 本金返还 = FFO（杠杆后）；'
  + '− CAPEX = AFFO；+ 现金调节及预留 = 可供分配金额。费率、比例默认取近一年实际；本金返还默认取近一年有息负债净减少额，还完为止；'
  + '现金调节第 0 期按系统分红率口径配平。FCFF / NAV 以市值 + 有息负债为买入价，NPV 扣除有息负债。';

// 自定义预测方式的输入项（每个预测年统一适用；单元格逐格修改优先）
export const CUSTOM_FIELDS = [
  { key: 'growth', label: '营业收入年增速' },
  { key: 'noi_margin', label: 'NOI Margin', stat: 'noi' },
  { key: 'ebitda_margin', label: 'EBITDA Margin', stat: 'ebitda' },
  { key: 'ffo_margin', label: 'FFO Margin', stat: 'ffo' },
  { key: 'div_margin', label: 'Div Margin', stat: 'distributable_abmod' },
];

// 折现的现金流都由现金流链算出；分派率 = 现金流 ÷ 市值（不从分红率倒推现金流）
export const CASH_BASES = [
  { value: 'distributable', label: '可供分配金额' },
  { value: 'ffo', label: 'FFO（全投资假设）' },
  { value: 'ebitda', label: 'EBITDA' },
  { value: 'noi', label: 'NOI' },
];

// 估值模型（PPT v2.7）：都是 DCF，区别在现金流与折现率的定法。calculator 决定折现率计算器显示哪组参数。
export const MODELS = [
  { value: 'ddm', label: 'DDM 估值', calculator: 'CAPM 模型', note: '可供分配金额按 CAPM 折现' },
  { value: 'fcff', label: 'FCFF 估值', calculator: 'WACC 计算', note: 'FFO（全投资假设）按 WACC 折现' },
  { value: 'nav', label: 'NAV 估值', calculator: 'Cap Rate 反算', note: 'NOI 按 Cap Rate + 永续增长率折现，仅适用产权类' },
];

// 折现率参数：ref = 基础数据 references 的键（rf 只有一个值，其余有本基金 own / 同业态 peer 两个参考）
export const RATE_PARAMS = {
  rf: { label: '无风险利率 Rf', ref: 'rf', refLabels: { value: '10年期国债' } },
  erp: { label: '风险溢价 ERP', ref: 'yield_spread', refLabels: { peer: '同业态历史 Yield Spread', own: '本基金 Yield Spread' } },
  beta: { label: 'Beta', ref: 'beta', ratio: false, refLabels: { peer: '同业态历史 beta', own: '本基金 beta' } },
  re_weight: { label: 'Re 占比' },
  rd: { label: 'Rd 成本' },
  cap_rate: { label: 'Cap Rate', ref: 'cap_rate', refLabels: { peer: '同类型业态', own: '本基金隐含' } },
  perp_growth: { label: '长期永续增长率', ref: 'growth', refLabels: { peer: '同业态实际增速', own: '本基金系统增速' } },
};
export const MODEL_PARAMS = {
  ddm: ['rf', 'erp', 'beta'],
  fcff: ['rf', 'erp', 'beta', 're_weight', 'rd'],
  nav: ['cap_rate', 'perp_growth'],
};

// 行快捷操作（行名旁的菜单）
export const ROW_ACTIONS = [
  { command: 'fill', label: '向右填充（以第一个预测年为准）' },
  { command: 'scale', label: '整行按比例调整…' },
  { command: 'copy', label: '复制本行' },
  { command: 'reset', label: '恢复本行默认' },
];

export const GROWTH_SOURCE_LABELS = {
  actual: '最新实际',
  forecast: '评估报告',
  linear: '线性外推',
  operating: '经营推算',
  custom: '自定义',
  system: '系统增速',
  override: '已修改',
};

export const LAST_SCENARIO = '__last__';
export const HELP_TOOLTIP_KEY = 'irr.calculator_help';
export const SOURCE_NOTE = '数据来源：葱花投研、基金定期报告及评估报告';
